/**
 * OAuth Callback Handler
 * Handles OAuth callbacks from MCP servers (Gmail, etc.)
 * Communicates with parent window to complete authentication flow
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { apiClient } from '../../services/api';

const OAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing authentication...');

  useEffect(() => {
    handleOAuthCallback();
  }, []);

  const handleOAuthCallback = async () => {
    try {
      // Extract OAuth parameters
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');
      
      console.log('OAuth Callback received:', {
        code: code ? 'present' : 'missing',
        state: state ? 'present' : 'missing',
        error,
        errorDescription,
        fullURL: window.location.href
      });

      // Check for OAuth errors
      if (error) {
        throw new Error(errorDescription || error);
      }

      // Validate state to prevent CSRF
      const storedState = sessionStorage.getItem('oauth_state');
      const mcpServerId = sessionStorage.getItem('oauth_mcp_server_id');
      
      if (!state || state !== storedState) {
        throw new Error('Invalid OAuth state. Please try again.');
      }

      if (!code) {
        throw new Error('No authorization code received');
      }

      // Extract tenant_id from state (format: tenantId_serverId_timestamp)
      const [tenantId] = state.split('_');

      // Exchange code for tokens via backend
      setMessage('Exchanging authorization code...');
      
      const response = await apiClient.post('/api/mcp/auth/callback', {
        code,
        state,
        tenant_id: tenantId,
        mcp_server_id: mcpServerId,
        redirect_uri: `${window.location.origin}/auth/callback`
      });

      if (response.data.success) {
        setStatus('success');
        setMessage('Authentication successful! You can close this window.');
        
        // Notify parent window of success
        if (window.opener) {
          window.opener.postMessage({
            type: 'oauth_callback',
            success: true,
            connection_id: response.data.connection_id,
            credentials: response.data.credentials
          }, window.location.origin);
          
          // Auto-close after 2 seconds
          setTimeout(() => {
            window.close();
          }, 2000);
        } else {
          // If no opener, redirect to external tools page
          setTimeout(() => {
            navigate('/external-tools');
          }, 2000);
        }
      } else {
        throw new Error(response.data.message || 'Failed to complete authentication');
      }

    } catch (error: any) {
      console.error('OAuth callback error:', error);
      setStatus('error');
      setMessage(error.message || 'Authentication failed');
      
      // Notify parent window of failure
      if (window.opener) {
        window.opener.postMessage({
          type: 'oauth_callback',
          success: false,
          error: error.message
        }, window.location.origin);
      }
      
      // Auto-close or redirect after 3 seconds
      setTimeout(() => {
        if (window.opener) {
          window.close();
        } else {
          navigate('/external-tools');
        }
      }, 3000);
    } finally {
      // Clean up session storage
      sessionStorage.removeItem('oauth_state');
      sessionStorage.removeItem('oauth_mcp_server_id');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-md w-full p-6">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold mb-2">OAuth Authentication</h1>
          <p className="text-muted-foreground">
            Completing authentication with MCP server
          </p>
        </div>

        <Alert className={
          status === 'success' ? 'border-green-500' : 
          status === 'error' ? 'border-red-500' : 
          'border-blue-500'
        }>
          <div className="flex items-center gap-3">
            {status === 'processing' && (
              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
            )}
            {status === 'success' && (
              <CheckCircle className="h-5 w-5 text-green-600" />
            )}
            {status === 'error' && (
              <XCircle className="h-5 w-5 text-red-600" />
            )}
            <AlertDescription className="flex-1">
              {message}
            </AlertDescription>
          </div>
        </Alert>

        {status === 'success' && (
          <p className="text-center text-sm text-muted-foreground mt-4">
            This window will close automatically...
          </p>
        )}
        
        {status === 'error' && (
          <div className="text-center mt-4">
            <p className="text-sm text-muted-foreground mb-2">
              You can close this window and try again
            </p>
            <button
              onClick={() => window.close()}
              className="text-sm text-primary hover:underline"
            >
              Close Window
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OAuthCallback;